import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Card } from '../components';
import { registerAgent } from '../api';
import { getPendingClaim } from './ClaimPage';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import Footer from '../components/Footer';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import PhoneInput from '../components/PhoneInput';

const countryCodes = [
  { code: '+31', label: '🇳🇱 +31 (NL)' },
  { code: '+32', label: '🇧🇪 +32 (BE)' },
  { code: '+49', label: '🇩🇪 +49 (DE)' },
  { code: '+44', label: '🇬🇧 +44 (UK)' },
  { code: '+33', label: '🇫🇷 +33 (FR)' },
  { code: '+34', label: '🇪🇸 +34 (ES)' },
  { code: '+1', label: '🇺🇸 +1 (US)' },
];

export const RegisterPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [countryCode, setCountryCode] = useState('+31');
  const [phone, setPhone] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setGlobalNotification } = useAuth();
  const { t } = useTranslation();

  function getFriendlyError(error: any) {
    const code = error?.code;
    const key = code ? `firebase_errors.${code}` : 'firebase_errors.default';
    const translation = t(key);
    return translation === key ? t('firebase_errors.default') : translation;
  }

  function validatePhone(number: string) {
    // Alleen cijfers, minimaal 8 cijfers
    return /^\d{8,15}$/.test(number);
  }

  function getPasswordStrength(pw: string) {
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return score;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    if (password !== confirmPassword) {
      setError(t('passwords_do_not_match', 'Passwords do not match.'));
      setLoading(false);
      return;
    }
    if (!validatePhone(phone)) {
      setError(t('invalid_phone', 'Please enter a valid phone number.'));
      setLoading(false);
      return;
    }
    try {
      const agentDetails = { email, companyName, contactPerson, phone: countryCode + phone };
      await registerAgent(agentDetails, password);
      setGlobalNotification({ message: t('success_register'), type: 'success' });
      const pending = getPendingClaim();
      navigate(pending ? `/claim/${pending.listingId}` : '/pending-approval');
    } catch (err: any) {
      const friendly = getFriendlyError(err);
      setError(friendly);
      setGlobalNotification({ message: friendly, type: 'error' });
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-neutral-100">
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="flex-1 flex items-center justify-center py-12">
        <Card className="w-full max-w-lg">
          <div className="p-8">
            <img src="/logo.png" alt="HomeMe Portaal Logo" className="h-20 w-auto mx-auto mb-6 rounded-[15px]" />
            <h2 className="text-2xl font-semibold text-center text-neutral-800 mb-2">{t('register')}</h2>
            <p className="text-sm text-center text-neutral-500 mb-6">{t('register_subtitle', 'Maak een account aan om toegang te krijgen tot het HomeMe portaal.')}</p>
            {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4 text-sm">{error}</p>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input label={t('company_name')} type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required autoComplete="organization"/>
              <Input label={t('contact_person')} type="text" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} required autoComplete="name"/>
              <Input label={t('email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"/>
              <PhoneInput
                countryCode={countryCode}
                setCountryCode={setCountryCode}
                phone={phone}
                setPhone={setPhone}
                required
                label={t('phone')}
                error={error && error.toLowerCase().includes('phone') ? error : undefined}
              />
              <Input label={t('password')} type="password" value={password} onChange={e => { setPassword(e.target.value); setPasswordStrength(getPasswordStrength(e.target.value)); }} minLength={6} required autoComplete="new-password"/>
              <div className="w-full h-2 bg-neutral-200 rounded mb-1">
                <div className={`h-2 rounded transition-all duration-300 ${passwordStrength === 0 ? 'bg-neutral-200 w-0' : passwordStrength === 1 ? 'bg-red-400 w-1/4' : passwordStrength === 2 ? 'bg-yellow-400 w-2/4' : passwordStrength === 3 ? 'bg-blue-400 w-3/4' : 'bg-green-500 w-full'}`}></div>
              </div>
              <Input label={t('confirm_password', 'Bevestig wachtwoord')} type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} minLength={6} required autoComplete="new-password"/>
              <Button type="submit" className="w-full" isLoading={loading} disabled={loading}>
                {t('register')}
              </Button>
            </form>
            <p className="text-sm text-center text-neutral-600 mt-6">
              {t('already_account')} <Link to="/login" className="font-medium text-homeme hover:underline">{t('login_here')}</Link>
            </p>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};
